import { useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "app/store";
import I18n from "common/i18n";
import { getPlanFullI18nKey } from "common/elements";
import { btnDelay, usePlaySound } from "common/utils";
import { PlanType } from "features/subscription/types";
import { usePlan, useRequestPurchase } from "features/subscription/hooks";
import { PlanCategory, BillingPeriod } from "features/subscription/useNativePackages";
import { AskParentModal } from "./AskParentModal";

type Props = {
  planCategory?: PlanCategory;
  billingPeriod?: BillingPeriod;
  variant: "card" | "dialog";
};

// 「保護者にお願いする」ボタン＋確認モーダルを内包した差し込み部品。
// プラン選択画面からは planCategory 付き、リミット到達ダイアログなどからはプランなしで使う。
// TRIAL（保護者未登録）や保護者メール不在では送信先がないため何も描画しない。
export const AskParentAction = ({ planCategory, billingPeriod, variant }: Props) => {
  const play = usePlaySound();
  const plan = usePlan();
  const parent = useSelector((state: RootState) => state.user.parent);
  const activeUser = useSelector((state: RootState) => state.user.active);
  const { send, isOnCooldown } = useRequestPurchase();
  const [modalOpen, setModalOpen] = useState(false);

  if (plan === PlanType.TRIAL || !parent?.email || !activeUser?.uid) return null;

  const childUid = activeUser.uid;
  const sent = isOnCooldown(childUid);
  const planLabel = planCategory
    ? I18n.t(getPlanFullI18nKey(planCategory === "standard" ? PlanType.STANDARD : PlanType.PREMIUM))
    : undefined;

  const handleClick = () => {
    play();
    btnDelay(() => setModalOpen(true));
  };

  const handleConfirm = () => {
    setModalOpen(false);
    send(childUid, planCategory, billingPeriod);
  };

  const btnLabel = I18n.t("MSG_SUBSCRIPTION_ASK_PARENT_BTN");
  const sentLabel = I18n.t("MSG_SUBSCRIPTION_ASK_PARENT_SENT_LABEL");

  let content;
  if (variant === "card") {
    content = sent ? (
      <div className="flex-row-el items-center justify-center gap-2 w-full box-border rounded-xl py-2 px-3 bg-gray2-100/10 border-2 border-solid border-gray2-100/30">
        <span className="material-symbols-outlined type-md text-gray2-100 shrink-0">check</span>
        <span className="type-text type-body text-gray2-100 leading-tight whitespace-nowrap">
          {sentLabel}
        </span>
      </div>
    ) : (
      <button
        type="button"
        onClick={handleClick}
        className="type-inherit-font flex-row-el items-center justify-center gap-2 w-full border-0 box-border rounded-xl py-2 px-3 bg-[#ffa03b] text-white shadow-[0_3px_0_#c86e1a,0_4px_10px_rgba(63,74,97,0.1)] active:translate-y-[2px] transition-transform cursor-pointer"
      >
        <span className="material-symbols-outlined type-md text-white shrink-0">
          volunteer_activism
        </span>
        <span className="type-text type-body text-white leading-tight whitespace-nowrap">
          {btnLabel}
        </span>
      </button>
    );
  } else {
    content = sent ? (
      <div className="flex-row-el items-center justify-center gap-2 box-border rounded-xl py-3 px-6 bg-gray2-100/10 border-2 border-solid border-gray2-100/30">
        <span className="material-symbols-outlined type-title text-gray2-100 shrink-0">check</span>
        <span className="type-text type-body text-gray2-100 leading-tight whitespace-nowrap">
          {sentLabel}
        </span>
      </div>
    ) : (
      <button
        type="button"
        onClick={handleClick}
        className="type-inherit-font flex-row-el items-center justify-center gap-2 border-0 box-border rounded-xl py-3 px-6 bg-[#ffa03b] text-white shadow-[0_3px_0_#c86e1a,0_4px_4px_-1px_rgba(0,0,0,0.6)] active:translate-y-[3px] transition-transform cursor-pointer"
      >
        <span className="material-symbols-outlined type-title text-white shrink-0">
          volunteer_activism
        </span>
        <p className="type-text type-body text-white whitespace-nowrap">{btnLabel}</p>
      </button>
    );
  }

  return (
    <>
      {content}
      <AskParentModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleConfirm}
        planLabel={planLabel}
      />
    </>
  );
};
